import React, { memo, useEffect, useState } from 'react';
import Timeline, {
  DateHeader,
  ReactCalendarGroupRendererProps,
  ReactCalendarItemRendererProps,
  TimelineGroupBase,
  TimelineHeaders,
  TimelineItemBase,
} from 'react-calendar-timeline';
import 'react-calendar-timeline/lib/Timeline.css';
import classNames from 'classnames/bind';
import styles from './BaseCalendarDay.module.scss';
import { Box } from '@mui/material';
import dayjs, { OpUnitType } from 'dayjs';

export type BaseCalendarDayProps<TGroup, TItem> = {
  date?: Date | string;
  groups: Array<BaseCalendarDayGroup<TGroup>>;
  items: Array<BaseCalendarDayItem<TItem>>;
  groupRenderer?: (group: BaseCalendarDayGroup<TGroup>) => React.ReactNode;
  itemRenderer?: (item: BaseCalendarDayItem<TItem>) => React.ReactNode;
  mode?: OpUnitType;
};

export type BaseCalendarDayGroup<TGroup> = TimelineGroupBase & {
  resource: TGroup;
};

export type BaseCalendarDayItem<TItem> = {
  resource: Array<TItem>;
} & TimelineItemBase<Date>;

const cx = classNames.bind(styles);
const modes: { [field: string]: OpUnitType } = {
  week: 'week',
  day: 'day',
};

const BaseCalendarDay = <TGroup extends Record<any, any>, TItem extends Record<any, any>>(
  props: BaseCalendarDayProps<TGroup, TItem>
) => {
  //#region Destructuring Props
  const { mode = modes.week } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [startTime, setStartTime] = useState(dayjs().startOf(mode).valueOf());
  const [endTime, setEndTime] = useState(dayjs().endOf(mode).valueOf());
  const [groups, setGroups] = useState<Array<BaseCalendarDayGroup<TGroup>>>([]);
  const [items, setItems] = useState<Array<BaseCalendarDayItem<TItem>>>([]);
  const [renderKey, setRenderKey] = useState(0); // Fix for itemRenderer update state
  //#endregion Declare State

  // //#region Implement Hook
  useEffect(() => {
    if (props.date) {
      setStartTime(dayjs(props.date).startOf(mode).valueOf());
      setEndTime(dayjs(props.date).endOf(mode).valueOf());
    }
  }, [mode, props.date]);

  useEffect(() => {
    setGroups(props.groups);
  }, [props.groups]);

  useEffect(() => {
    setItems(props.items);
  }, [props.items]);

  useEffect(() => {
    setRenderKey(Date.now());
  }, [items]);
  //#endregion Implement Hook

  //#region Handle Function
  const groupRenderer = (calendarProps: ReactCalendarGroupRendererProps<BaseCalendarDayGroup<TGroup>>) => {
    return props?.groupRenderer ? (
      props?.groupRenderer(calendarProps.group)
    ) : (
      <Box>{calendarProps?.group?.resource?.name}</Box>
    );
  };

  const itemRenderer = (calendarProps: ReactCalendarItemRendererProps<BaseCalendarDayItem<TItem>>) => {
    return (
      <Box {...calendarProps.getItemProps({})} key={calendarProps?.item?.id}>
        {props?.itemRenderer
          ? props?.itemRenderer(calendarProps.item)
          : calendarProps?.item?.resource.map(({ name }) => name).join(', ')}
      </Box>
    );
  };
  //#endregion Handle Function

  return (
    <Timeline
      groups={groups}
      items={items}
      visibleTimeStart={startTime}
      visibleTimeEnd={endTime}
      groupRenderer={groupRenderer}
      itemRenderer={itemRenderer}
      className={cx('base-calendar-day')}
      lineHeight={100}
      canMove={false}
      canChangeGroup={false}
      canResize={false}
      stackItems
      itemTouchSendsClick={false}
      sidebarWidth={180}
      itemHeightRatio={1}
      key={renderKey}
    >
      <TimelineHeaders className='sticky top-0 z-[1000]'>
        <DateHeader labelFormat={mode === modes.day ? 'HH A' : 'dddd'} height={39} />
      </TimelineHeaders>
    </Timeline>
  );
};

export default memo(BaseCalendarDay) as typeof BaseCalendarDay;
